import {
  json,
  type LoaderFunctionArgs,
  type MetaFunction,
} from '@remix-run/node'
import { Link, useLoaderData } from '@remix-run/react'
import { useTranslation } from 'react-i18next'

import { GeneralErrorBoundary, NotFoundError } from '#app/components/errors.tsx'
import { Layout } from '#app/components/layout.tsx'
import { RichText } from '#app/components/rich-text.tsx'
import { Button } from '#app/components/ui/button.tsx'
import type { RootLoaderType } from '#app/root.tsx'
import type { Handle } from '#app/types/index.ts'
import { defaultLocale, getLocaleFromRequest } from '#app/utils/i18n.ts'
import { mapImage } from '#app/utils/mappers.ts'
import { getUrl } from '#app/utils/misc.tsx'
import { getSocialMetas } from '#app/utils/seo.ts'
import { getAllVacancies, getVacancyBySlug } from '#app/utils/strapi.ts'

import { routes as jobsRoutes } from './vacatures._index.tsx'

export const routes: Record<string, string> = {
  nl: 'solliciteren',
  en: 'apply',
}

export const handle: Handle = {
  getSitemapEntries: async request => {
    const locale = getLocaleFromRequest(request)
    const pages = await getAllVacancies(locale)
    return (pages || []).map(page => ({
      route: `${locale === defaultLocale ? '' : `/${locale}`}/${jobsRoutes[locale]}/${page.attributes.slug}`,
      priority: 0.6,
    }))
  },
}

export async function loader({ request, params }: LoaderFunctionArgs) {
  if (!params.slug) {
    throw new Error('No slug provided to fetch vacancy with.')
  }

  const locale = getLocaleFromRequest(request)
  const vacancy = await getVacancyBySlug(locale, params.slug)

  if (!vacancy) {
    throw new Response('Not found', { status: 404 })
  }

  return json({
    vacancy,
  })
}

export const meta: MetaFunction<typeof loader, { root: RootLoaderType }> = ({
  data,
  matches,
}) => {
  const rootData = matches.find(m => m.id === 'root')?.data

  if (data?.vacancy?.SEO) {
    const meta = data.vacancy.SEO
    return getSocialMetas({
      title: meta?.title,
      description: meta?.description,
      image: meta?.image.data?.attributes.url,
      url: getUrl(rootData.requestInfo),
    })
  } else {
    return [
      { title: 'Not Found' },
      { name: 'description', content: 'Not found' },
    ]
  }
}

export default function VacaturePage() {
  const data = useLoaderData<typeof loader>()
  const { t, i18n } = useTranslation()

  const image = mapImage(data.vacancy.image.data?.attributes)

  return (
    <Layout imageSrc={image.url} imageAlt={image.alt}>
      <h1 className="mb-8 block text-h2 text-primary">{data.vacancy.title}</h1>
      <RichText content={data.vacancy.description} />
      <Button className="mt-4" asChild>
        <Link
          to={`${i18n.language === defaultLocale ? '' : '/en'}/${jobsRoutes[i18n.language]}/${data.vacancy.slug}/${routes[i18n.language]}`}
        >
          {t('cta.apply')}
        </Link>
      </Button>
    </Layout>
  )
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary statusHandlers={{ 404: NotFoundError }} />
}
